/**
 * Media library job contract (spec 30 §4.4): the API enqueues `process-asset`
 * on queue `media` after an upload is finalized; the worker processor
 * (`processors/media/process-asset.processor.ts`, S30-2) derives the variants.
 */
export const MEDIA_PROCESS_ASSET_JOB_NAME = 'process-asset';
/** BullMQ job ID of a processing run (spec 30 §4.4); `n` counts reprocessing requests. */
export function processAssetJobId(assetId, attempt) {
    return `${MEDIA_PROCESS_ASSET_JOB_NAME}:${assetId}:${attempt}`;
}
/**
 * Import of the images and videos already posted on the property's Instagram and
 * Facebook accounts (spec 30 section 4.13). The API enqueues one job per run on
 * queue `media`; the worker pages through the accounts and stores each new file
 * as an `own` asset.
 */
export const MEDIA_SOCIAL_IMPORT_JOB_NAME = 'social-import';
/** BullMQ job ID of an import run: one job per run. */
export function socialImportJobId(importId) {
    return `${MEDIA_SOCIAL_IMPORT_JOB_NAME}-${importId}`;
}
/**
 * Sweep that applies pending AI suggestions (spec 30 section 4.18, S280-1): the scheduled
 * `sweep-pending-suggestions` (at worker start, then hourly) lists the assets of every
 * tenant that still have a pending tag, description, season or quality suggestion and
 * queues one `apply-pending-suggestions` job per asset on queue `media`.
 */
export const MEDIA_SWEEP_SUGGESTIONS_JOB_NAME = 'sweep-pending-suggestions';
export const MEDIA_APPLY_SUGGESTIONS_JOB_NAME = 'apply-pending-suggestions';
/** BullMQ job ID of an application: the asset ID, so a repeated sweep adds nothing while one is queued. */
export function applyPendingSuggestionsJobId(assetId) {
    return assetId;
}
export const SOCIAL_IMPORT_STATUSES = ['queued', 'running', 'done', 'failed'];
export const SOCIAL_IMPORT_PLATFORMS = ['instagram', 'facebook'];
/** Why one file was not taken over (`failures[].reason`). */
export const SOCIAL_IMPORT_FAILURE_REASONS = ['unreadable', 'not_an_image', 'not_a_video', 'too_large', 'media_unavailable', 'storage_failed'];
export const SOCIAL_IMPORT_LIMITS = {
    /** New assets one run creates; a later run continues with what is still missing. */
    maxImportsPerRun: 1000,
    /** Media entries one account is read for in one run: Meta's own ceiling of the Instagram media edge (10 000). */
    maxScannedPerAccount: 10_000,
    /** Entries per Graph page. */
    pageSize: 25,
    /** Failed files kept in the run for display; the count is exact. */
    maxFailuresListed: 50,
    /** A run not touched for this long is abandoned (worker lost) and may be replaced. */
    staleAfterMinutes: 30,
};
/**
 * Resolution classes of the picture choice (spec 30 section 4.19, E280): by the short
 * side in pixels. Provisional: Meta's own minimum/maximum widths are unverified
 * (T280-2-1, checked 2026-10-13: the developer reference renders client-side and could not be read
 * by the agent; 1080 px is the commonly recommended width and module 90's canvas).
 */
export const MEDIA_RESOLUTION = { good: 1080, ok: 720 };
/** The class of a picture whose short side is `shortSide` px; an unknown size is not penalised (`ok`). */
export function resolutionClassOf(shortSide) {
    if (shortSide === null)
        return 'ok';
    if (shortSide >= MEDIA_RESOLUTION.good)
        return 'good';
    return shortSide >= MEDIA_RESOLUTION.ok ? 'ok' : 'low';
}
export const MEDIA_TOPIC_MAX_TERMS = 12;
const TOPIC_MIN_LETTERS = 4;
const TOPIC_STOP_WORDS = new Set([
    'und', 'oder', 'aber', 'auch', 'nach', 'vor', 'mit', 'ohne', 'fuer', 'f\u00fcr', 'von', 'dem', 'den', 'der', 'die', 'das', 'des', 'ein', 'eine', 'einen', // lint-code-language: ignore (German stopwords)
    'einem', 'einer', 'dass', 'wie', 'wir', 'ihr', 'ihre', 'euer', 'unser', 'unsere', 'dein', 'mein', 'sind', 'wird', 'werden', 'wurde', 'noch', 'schon', // lint-code-language: ignore (German stopwords)
    'nur', 'mehr', 'sehr', 'beim', 'zum', 'zur', 'ins', 'bei', 'aus', 'durch', 'ueber', '\u00fcber', 'unter', 'diese', 'dieser', 'dieses', 'jetzt', 'heute', // lint-code-language: ignore (German stopwords)
    'with', 'from', 'that', 'this', 'your', 'have', 'will', 'into', 'about', 'after', 'before', 'and', 'the', 'for', 'our',
]);
/**
 * The search terms of a post topic for the ranking of candidates (spec 30 section 4.19): words of
 * at least four letters from title and description, lower case, without stop words, each once, at most 12.
 */
export function topicTerms(title, description) {
    const words = `${title} ${description ?? ''}`.toLowerCase().match(/\p{L}+/gu) ?? [];
    const terms = [];
    for (const word of words) {
        if (word.length < TOPIC_MIN_LETTERS || TOPIC_STOP_WORDS.has(word) || terms.includes(word))
            continue;
        terms.push(word);
        if (terms.length === MEDIA_TOPIC_MAX_TERMS)
            break;
    }
    return terms;
}
/**
 * Trash and purge of media (spec 30 section 4.14). A trashed asset can be restored for this many
 * days (fixed, no setting); then the hourly sweep `purge-trashed-assets` queues a `purge-asset` job.
 */
export const MEDIA_TRASH_RETENTION_DAYS = 30;
export const MEDIA_PURGE_TRASHED_JOB_NAME = 'purge-trashed-assets';
export const MEDIA_PURGE_ASSET_JOB_NAME = 'purge-asset';
/** BullMQ job ID of a purge: one per asset; the delayed second pass has its own. */
export function purgeAssetJobId(assetId, sweep = false) {
    return `${sweep ? 'purge-asset-sweep' : MEDIA_PURGE_ASSET_JOB_NAME}:${assetId}:once`;
}
/** The second pass runs after the longest possible `process-asset` run (5 minutes) plus a minute. */
export const PURGE_SWEEP_DELAY_MS = 6 * 60 * 1000;
//# sourceMappingURL=media.js.map